import type { DropEdge, ReorderableTreeNode } from "@v1/types/tree";

type TreeNodeData = {
  node: ReorderableTreeNode;
  order: number;
  depth: number;
};

export type GetDropTargetParams = {
  clientX: number;
  hoveredNodeId: string;
  side: DropEdge;
  idToNodeData: Record<string, TreeNodeData>;
  idToParentNode: Record<string, ReorderableTreeNode | null>;
  explicitExpandedNodeIds: string[];
  getTreeNodeLabelElement: (id: string) => HTMLElement | null;
  isDroppable: (id: string) => boolean;
};

type DropTargetResult = {
  previewId: string;
  dropParentId: string;
  dropPosition: number;
};

function computeInitialDropTarget({
  hoveredNode,
  hoveredParentNode,
  side,
  idToParentNode,
  explicitExpandedNodeIds,
  isDroppable,
}: {
  hoveredNode: ReorderableTreeNode;
  hoveredParentNode: ReorderableTreeNode;
  side: DropEdge;
  idToParentNode: Record<string, ReorderableTreeNode | null>;
  explicitExpandedNodeIds: string[];
  isDroppable: (id: string) => boolean;
}): { previewId: string; dropParentId: string; dropPosition: number } {
  const index = hoveredParentNode.children.findIndex(
    (node) => node.id === hoveredNode.id,
  );

  if (side === "beforeCurrentNode") {
    if (index === 0) {
      return {
        previewId: hoveredParentNode.id,
        dropParentId: hoveredParentNode.id,
        dropPosition: 0,
      };
    }

    let leftNotExpandedChild: ReorderableTreeNode =
      hoveredParentNode.children[index - 1]!;
    let dropPosition = index;

    while (explicitExpandedNodeIds.includes(leftNotExpandedChild.id)) {
      const length = leftNotExpandedChild.children.length;
      if (!length) {
        break;
      }
      leftNotExpandedChild = leftNotExpandedChild.children[length - 1]!;
      dropPosition = length;
    }

    // Note (Ovishek, 2022-06-16): we all know that leftNotExpandedChild is always someones child,
    // so it's safe to assert that, its parent exists!
    return {
      previewId: leftNotExpandedChild.id,
      dropParentId: idToParentNode[leftNotExpandedChild.id]!.id,
      dropPosition,
    };
  }

  const shouldGoInside =
    (isDroppable(hoveredNode.id) && side === "middle") ||
    (explicitExpandedNodeIds.includes(hoveredNode.id) &&
      hoveredNode.children.length > 0 &&
      side === "afterCurrentNode");

  return {
    previewId: hoveredNode.id,
    dropParentId: shouldGoInside ? hoveredNode.id : hoveredParentNode.id,
    dropPosition: shouldGoInside ? 0 : index + 1,
  };
}

export function getDropTargetAndPreviewId({
  clientX,
  hoveredNodeId,
  side,
  idToNodeData,
  idToParentNode,
  explicitExpandedNodeIds,
  getTreeNodeLabelElement,
  isDroppable,
}: GetDropTargetParams): DropTargetResult | null {
  // NOTE (Patrick, 2026-03-02): null guard for when tree data is
  // stale during a mid-drag re-render — the hovered node may no longer exist
  const hoveredNodeData = idToNodeData[hoveredNodeId];
  if (!hoveredNodeData) {
    return null;
  }
  const hoveredNode = hoveredNodeData.node;
  const hoveredParentNode = idToParentNode[hoveredNodeId];
  if (!hoveredParentNode) {
    return {
      previewId: hoveredNode.id,
      dropParentId: hoveredNode.id,
      dropPosition: 0,
    };
  }

  let { previewId, dropParentId, dropPosition } = computeInitialDropTarget({
    hoveredNode,
    hoveredParentNode,
    side,
    idToParentNode,
    explicitExpandedNodeIds,
    isDroppable,
  });

  if (
    dropPosition &&
    dropPosition === idToNodeData[dropParentId]!.node.children.length
  ) {
    let currentId =
      idToNodeData[dropParentId]!.node.children[dropPosition - 1]!.id;

    while (idToParentNode[currentId]) {
      const parent = idToParentNode[currentId];
      if (!parent) {
        break;
      }
      const isCurrentLastChild =
        parent.children[parent.children.length - 1]!.id === currentId;

      // NOTE (Patrick, 2026-03-02): null guard for when DOM refs are
      // stale — tree nodes may have unmounted mid-drag due to re-render
      const currentTreeNodeLabel = getTreeNodeLabelElement(currentId);
      if (!currentTreeNodeLabel) {
        break;
      }
      const currentDistanceLeft =
        currentTreeNodeLabel.getBoundingClientRect().left || 0;

      if (
        clientX <= currentDistanceLeft &&
        isCurrentLastChild &&
        !explicitExpandedNodeIds.includes(previewId) &&
        idToParentNode[parent.id]
      ) {
        currentId = parent.id;
        dropParentId = idToParentNode[parent.id]!.id;
        dropPosition =
          idToParentNode[parent.id]!.children.findIndex(
            (node) => node.id === parent.id,
          ) + 1;
      } else {
        break;
      }
    }
  }

  return {
    previewId,
    dropParentId,
    dropPosition,
  };
}
